import { sectionRenderer } from '@theme/section-renderer';

/**
 * ChunkedScrollManager - Manages infinite scroll with bounded DOM
 *
 * Features:
 * - Max 2 chunks in DOM at once (configurable)
 * - Bidirectional loading (up/down)
 * - Chunk caching (max 3 chunks)
 * - State persistence via sessionStorage
 * - Fast-back restoration
 */
export default class ChunkedScrollManager {
  /** @type {number} - Maximum chunks to keep in DOM */
  static MAX_CHUNKS_IN_DOM = 2;

  /** @type {number} - Maximum chunks to cache in memory */
  static MAX_CACHE_SIZE = 3;

  /** @type {number} - Products per chunk */
  static PRODUCTS_PER_CHUNK = 36;

  /** @type {number} - Debounce delay for scroll events (ms) */
  static DEBOUNCE_DELAY = 300;

  /** @type {Map<number, string>} - Cache of fetched chunk HTML */
  #chunkCache = new Map();

  /** @type {Array<{page: number, element: HTMLElement}>} - Chunks currently in DOM */
  #chunksInDom = [];

  /** @type {IntersectionObserver | null} */
  #topObserver = null;

  /** @type {boolean} - Lock to prevent duplicate fetches */
  #fetchingNext = false;

  /** @type {boolean} - Lock to prevent duplicate fetches */
  #fetchingPrevious = false;

  /** @type {boolean} - Flag to prevent auto-load on initial render */
  #hasUserInteracted = false;

  /** @type {boolean} - Temporary flag to prevent auto-load immediately after eviction */
  #justEvicted = false;

  /** @type {number} - Total pages in collection */
  #totalPages = 1;

  /** @type {AbortController | null} - Controller for canceling in-flight requests */
  #abortController = null;

  /** @type {string} - Collection handle */
  #collectionHandle = '';

  /** @type {string} - Section ID for Section Rendering API */
  #sectionId = '';

  /** @type {Function} - Aspect ratio processor from parent */
  #processNewElements = null;

  /** @type {Object} - DOM element references */
  refs = {
    grid: null,
    topSentinel: null,
    bottomSentinel: null,
    loadingIndicator: null,
    topLoadingIndicator: null,
    endIndicator: null,
    loadMoreContainer: null,
    loadMoreButton: null,
  };

  /** @type {HTMLElement} - Parent results list element */
  #parentElement = null;

  /**
   * Constructor
   * @param {Object} config - Configuration object
   * @param {number} config.totalPages - Total pages in collection
   * @param {string} config.sectionId - Section ID for Section Rendering API
   * @param {string} config.collectionHandle - Collection handle
   * @param {HTMLElement} config.parentElement - Parent element for event listening
   */
  constructor(config) {
    this.#totalPages = config.totalPages || 1;
    this.#sectionId = config.sectionId || '';
    this.#collectionHandle = config.collectionHandle || '';
    this.#parentElement = config.parentElement;
  }

  /**
   * Initialize the chunked scroll manager
   */
  init() {
    // Disable browser's default scroll restoration to use our custom logic
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // Store reference to initial chunk
    const { grid } = this.refs;
    if (grid) {
      const initialPage = this.#getCurrentPageFromDOM();
      this.#chunksInDom.push({ page: initialPage, element: grid });
    } else {
      console.error('[chunked-scroll] Grid element not found in refs!');
    }

    // Setup intersection observers
    this.#setupObservers();

    // Setup product click tracking for fast-back navigation
    this.#setupProductClickTracking();

    // Restore state if navigating back
    this.#restoreState();

    // Listen for custom events
    if (this.#parentElement) {
      this.#parentElement.addEventListener('chunked-scroll:reset', this.#handleReset);
    }

    // Listen for bfcache restoration (iOS Safari swipe-back)
    window.addEventListener('pageshow', this.#handlePageShow);

    // Enable upward auto-loading after a delay (for deep link pages)
    // Only do this if we're starting on a page > 1 (deep link scenario)
    // This prevents cascade loading on initial render, but allows auto-load after user has time to orient
    const initialPage = this.#getCurrentPageFromDOM();
    if (initialPage > 1) {
      setTimeout(async () => {
        this.#hasUserInteracted = true;

        // Check if top sentinel is currently in view - if so, trigger load of previous chunk
        const { topSentinel } = this.refs;
        if (topSentinel) {
          const rect = topSentinel.getBoundingClientRect();
          const isInView = rect.top >= -200 && rect.top <= window.innerHeight + 200; // Use same rootMargin as observer

          if (isInView && !this.#fetchingPrevious) {
            await this.#loadPreviousChunk();
          }
        }
      }, 3000); // 3 second delay
    }

  }

  /**
   * Cleanup and destroy
   */
  destroy() {
    this.#cleanup();
  }

  /**
   * Setup intersection observers for top sentinel and Load More button
   */
  #setupObservers() {
    const { topSentinel, loadMoreButton, loadMoreContainer } = this.refs;

    // Setup Load More button click handler (replaces bottom observer)
    if (loadMoreButton) {
      loadMoreButton.addEventListener('click', async (event) => {
        event.preventDefault(); // Prevent any default scroll behavior
        this.#hasUserInteracted = true; // Enable upward auto-loading
        await this.#loadNextChunk();
      });

      // Show button initially if not on last page
      const currentPage = this.#getCurrentPageFromDOM();
      if (currentPage < this.#totalPages && loadMoreContainer) {
        loadMoreContainer.style.display = 'flex';
      }
    } else {
      console.error('[chunked-scroll] Load More button not found!');
    }

    // Setup top observer for auto-loading previous chunks
    if (topSentinel) {
      this.#topObserver = new IntersectionObserver(
        (entries) => this.#handleTopIntersection(entries),
        { rootMargin: '200px' }
      );
      this.#topObserver.observe(topSentinel);
    } else {
    }
  }

  /**
   * Setup product click tracking for fast-back navigation
   */
  #setupProductClickTracking() {
    const { grid } = this.refs;
    if (!grid) {
      return;
    }


    // Use event delegation to track all product card clicks
    grid.addEventListener('click', (event) => {

      // Find the closest product card first (any click within the card)
      const productCard = event.target.closest('[data-product-id]');

      if (!productCard) {
        return;
      }

      // Always store if we found a product card - navigation will happen via JS or link
      // We'll let the browser handle the navigation and just track the product ID

      // Store the clicked product ID for fast-back restoration
      const productId = productCard.getAttribute('data-product-id');
      const key = `chunked-scroll:clicked-product:${window.location.pathname}`;

      try {
        sessionStorage.setItem(key, JSON.stringify({
          productId,
          timestamp: Date.now()
        }));
      } catch (error) {
        console.error('[chunked-scroll] Failed to store clicked product:', error);
      }
    });

  }

  /**
   * Handle top sentinel intersection (load previous chunk)
   * @param {IntersectionObserverEntry[]} entries
   */
  async #handleTopIntersection(entries) {
    for (const entry of entries) {
      // Only auto-load if user has interacted and we didn't just evict
      // (prevents auto-load on initial render and immediately after eviction)
      if (entry.isIntersecting && !this.#fetchingPrevious && this.#hasUserInteracted && !this.#justEvicted) {
        await this.#loadPreviousChunk();
      }
    }
  }

  /**
   * Load the next chunk of products
   */
  async #loadNextChunk() {
    // LOCK scroll position - save it immediately
    const lockedScrollPosition = window.scrollY;

    const nextPage = this.#getNextPage();

    if (!nextPage || nextPage > this.#totalPages) {
      this.#hideLoadMoreButton();
      this.#showEndIndicator();
      return;
    }

    this.#fetchingNext = true;

    // Temporarily disable smooth scrolling behavior
    const htmlElement = document.documentElement;
    const originalScrollBehavior = htmlElement.style.scrollBehavior;
    htmlElement.style.scrollBehavior = 'auto';

    this.#hideLoadMoreButton();
    this.#showLoading(true);

    // Immediately restore scroll after UI changes
    window.scrollTo(0, lockedScrollPosition);

    try {
      const chunkHTML = await this.#fetchChunk(nextPage);
      if (!chunkHTML) return;

      // Restore scroll before append
      window.scrollTo(0, lockedScrollPosition);

      await this.#appendChunk(nextPage, chunkHTML, lockedScrollPosition);

      // Restore scroll after append
      window.scrollTo(0, lockedScrollPosition);

      this.#enforceMaxChunks('bottom');

      // UPDATE locked position after eviction (eviction may have adjusted scroll)
      const scrollAfterEviction = window.scrollY;

      this.#saveState();

      // Show button again if more pages exist
      if (nextPage < this.#totalPages) {
        this.#showLoadMoreButton();
      } else {
        this.#showEndIndicator();
      }

      // Restore scroll after showing button (use adjusted position)
      window.scrollTo(0, scrollAfterEviction);

      // Final restoration in next frame
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollAfterEviction);

        // Restore scroll behavior
        htmlElement.style.scrollBehavior = originalScrollBehavior;
      });

    } catch (error) {
      console.error('Error loading next chunk:', error);
      this.#showRetry('next');
      htmlElement.style.scrollBehavior = originalScrollBehavior;
    } finally {
      this.#fetchingNext = false;
      this.#showLoading(false);

      // Don't restore here - eviction may have adjusted scroll intentionally
    }
  }

  /**
   * Load the previous chunk of products
   */
  async #loadPreviousChunk() {
    const previousPage = this.#getPreviousPage();

    if (!previousPage || previousPage < 1) {
      return;
    }

    this.#fetchingPrevious = true;
    this.#showTopLoading(true);

    try {
      const chunkHTML = await this.#fetchChunk(previousPage);
      if (!chunkHTML) return;

      await this.#prependChunk(previousPage, chunkHTML);
      this.#enforceMaxChunks('top');
      this.#saveState();

    } catch (error) {
      console.error('Error loading previous chunk:', error);
      this.#showRetry('previous');
    } finally {
      this.#fetchingPrevious = false;
      this.#showTopLoading(false);
    }
  }

  /**
   * Fetch a chunk from server or cache
   * @param {number} page - Page number to fetch
   * @returns {Promise<string|null>} - Chunk HTML or null if error
   */
  async #fetchChunk(page) {
    // Check cache first
    if (this.#chunkCache.has(page)) {
      return this.#chunkCache.get(page);
    }

    // Cancel any in-flight request
    if (this.#abortController) {
      this.#abortController.abort();
    }

    this.#abortController = new AbortController();

    try {
      // Build URL for Section Rendering API
      const url = new URL(window.location.href);
      url.searchParams.set('page', page.toString());
      url.hash = '';

      // Fetch using Section Rendering API
      const html = await sectionRenderer.getSectionHTML(
        this.#sectionId,
        true,
        url
      );

      // Cache the result
      this.#addToCache(page, html);

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }
      throw error;
    }
  }

  /**
   * Append a chunk to the DOM
   * @param {number} page - Page number
   * @param {string} html - Chunk HTML
   * @param {number} lockedScrollPosition - Scroll position to maintain
   */
  async #appendChunk(page, html, lockedScrollPosition) {
    const { grid } = this.refs;
    if (!grid) return;

    // Parse HTML and extract product items
    const products = this.#extractProductsFromHTML(html);
    if (!products || products.length === 0) {
      this.#showEndIndicator();
      return;
    }

    // Append products to grid
    grid.append(...products);

    // IMMEDIATELY restore scroll to locked position
    window.scrollTo(0, lockedScrollPosition);

    // Track this chunk
    this.#chunksInDom.push({ page, element: grid });

    // Process new elements (aspect ratios, etc.)
    if (this.#processNewElements) {
      this.#processNewElements();
    }

    // Restore scroll again after aspect ratio processing
    window.scrollTo(0, lockedScrollPosition);

    // And once more in the next frame
    requestAnimationFrame(() => {
      window.scrollTo(0, lockedScrollPosition);
    });

    // Update URL without reload
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('page', page.toString());
    history.replaceState({ page }, '', newUrl.toString());
  }

  /**
   * Prepend a chunk to the DOM
   * @param {number} page - Page number
   * @param {string} html - Chunk HTML
   */
  async #prependChunk(page, html) {
    const { grid } = this.refs;
    if (!grid) return;

    // Parse HTML and extract product items
    const products = this.#extractProductsFromHTML(html);
    if (!products || products.length === 0) return;

    // Store scroll position before prepend
    const scrollTop = window.scrollY;

    // Find the first VISIBLE product (not behind header, not off-screen)
    // This ensures we track something the user can actually see
    let referenceElement = null;
    let referenceTop = 0;

    const allProducts = Array.from(grid.querySelectorAll('[ref="cards[]"]'));
    for (const product of allProducts) {
      const rect = product.getBoundingClientRect();
      // Find first product that's actually visible (below header, in viewport)
      // Assuming header is ~100px, look for products with top > 50px
      if (rect.top >= 50 && rect.top < window.innerHeight) {
        referenceElement = product;
        referenceTop = rect.top;
        break;
      }
    }

    // Fallback to first product if no visible product found
    if (!referenceElement) {
      referenceElement = allProducts[0];
      referenceTop = referenceElement?.getBoundingClientRect().top || 0;
    }

    // Prepend products to grid
    grid.prepend(...products);

    // Track this chunk
    this.#chunksInDom.unshift({ page, element: grid });

    // Process new elements (aspect ratios, etc.)
    if (this.#processNewElements) {
      this.#processNewElements();
    }

    // Force a reflow by reading layout property
    void grid.offsetHeight;

    // Adjust scroll position to prevent jump
    // Note: Browser scroll anchoring may interfere with immediate measurement,
    // so we adjust in both immediate and RAF contexts
    const adjustScroll = () => {
      if (referenceElement) {
        const newTop = referenceElement.getBoundingClientRect().top;
        const scrollOffset = newTop - referenceTop;
        const targetScroll = Math.max(0, scrollTop + scrollOffset);

        window.scrollTo({
          top: targetScroll,
          behavior: 'instant',
        });
      }
    };

    // Try adjusting immediately
    adjustScroll();

    // Also adjust in RAF after browser scroll anchoring releases
    requestAnimationFrame(() => {
      adjustScroll();
    });

    // Update URL without reload
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('page', page.toString());
    history.replaceState({ page }, '', newUrl.toString());
  }

  /**
   * Extract product items from fetched HTML
   * @param {string} html - Section HTML
   * @returns {Element[] | null} - Array of product card elements
   */
  #extractProductsFromHTML(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const grid = doc.querySelector('[ref="grid"]');

    if (!grid) return null;

    return Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
  }

  /**
   * Enforce maximum chunks in DOM by removing oldest/newest
   * @param {'top' | 'bottom'} direction - Which direction we're loading
   */
  #enforceMaxChunks(direction) {
    const { grid } = this.refs;
    if (!grid) return;

    const maxChunks = ChunkedScrollManager.MAX_CHUNKS_IN_DOM;

    while (this.#chunksInDom.length > maxChunks) {
      // Remove from opposite end of loading direction
      const chunkToRemove = direction === 'bottom'
        ? this.#chunksInDom.shift()  // Remove from top
        : this.#chunksInDom.pop();   // Remove from bottom

      if (!chunkToRemove) continue;

      const removingFromTop = direction === 'bottom';

      // If removing from top, find a reference element in the remaining content
      let referenceElement = null;
      let referenceTop = 0;

      if (removingFromTop) {
        // Find first product that's NOT being removed (first product in chunk 2)
        const remainingProducts = grid.querySelectorAll(`[data-page]:not([data-page="${chunkToRemove.page}"])`);
        if (remainingProducts.length > 0) {
          referenceElement = remainingProducts[0];
          referenceTop = referenceElement.getBoundingClientRect().top;
        }
      }

      // Remove products from DOM
      const productsToRemove = grid.querySelectorAll(
        `[data-page="${chunkToRemove.page}"]`
      );


      productsToRemove.forEach((product) => {
        product.remove();
      });

      // Adjust scroll to keep reference element at same visual position
      if (removingFromTop && referenceElement) {
        const newTop = referenceElement.getBoundingClientRect().top;
        const topDifference = newTop - referenceTop;

        if (Math.abs(topDifference) > 1) {
          const currentScroll = window.scrollY;
          const newScroll = Math.max(0, currentScroll + topDifference);


          window.scrollTo({
            top: newScroll,
            behavior: 'instant',
          });
        }
      }

      // Nullify references to help GC
      chunkToRemove.element = null;
    }

    // Prevent auto-load from triggering immediately after eviction
    // (the scroll adjustment might bring the top sentinel into view)
    if (this.#chunksInDom.length === ChunkedScrollManager.MAX_CHUNKS_IN_DOM) {
      this.#justEvicted = true;
      setTimeout(() => {
        this.#justEvicted = false;
      }, 500); // Reset after 500ms
    }
  }

  /**
   * Add chunk to cache with LRU eviction
   * @param {number} page - Page number
   * @param {string} html - Chunk HTML
   */
  #addToCache(page, html) {
    // If cache is full, remove oldest entry (first item in Map)
    if (this.#chunkCache.size >= ChunkedScrollManager.MAX_CACHE_SIZE) {
      const firstKey = this.#chunkCache.keys().next().value;
      this.#chunkCache.delete(firstKey);
    }

    this.#chunkCache.set(page, html);
  }

  /**
   * Get the next page number to load
   * @returns {number | null}
   */
  #getNextPage() {
    if (this.#chunksInDom.length === 0) return null;

    const lastChunk = this.#chunksInDom[this.#chunksInDom.length - 1];
    return lastChunk.page + 1;
  }

  /**
   * Get the previous page number to load
   * @returns {number | null}
   */
  #getPreviousPage() {
    if (this.#chunksInDom.length === 0) return null;

    const firstChunk = this.#chunksInDom[0];
    return firstChunk.page - 1;
  }

  /**
   * Get current page number from DOM
   * @returns {number}
   */
  #getCurrentPageFromDOM() {
    const { grid } = this.refs;
    if (!grid) return 1;

    const firstCard = grid.querySelector('[data-page]');
    return parseInt(firstCard?.getAttribute('data-page') || '1', 10);
  }

  /**
   * Save current state to sessionStorage
   */
  #saveState() {
    try {
      const state = {
        pages: this.#chunksInDom.map(c => c.page),
        scrollOffset: window.scrollY,
        timestamp: Date.now(),
      };

      const key = `chunked-scroll:${window.location.pathname}`;
      sessionStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save scroll state:', error);
    }
  }

  /**
   * Restore state from sessionStorage (for fast-back)
   */
  #restoreState() {
    try {
      // PRIORITY 1: Check for clicked product (fast-back navigation)
      const productKey = `chunked-scroll:clicked-product:${window.location.pathname}`;
      const productStr = sessionStorage.getItem(productKey);

      if (productStr) {
        const productData = JSON.parse(productStr);

        // Only restore if recent (within 5 minutes)
        if (Date.now() - productData.timestamp <= 5 * 60 * 1000) {

          // Poll for the product element (DOM might not be ready yet)
          const maxAttempts = 30; // 1.5 seconds max (30 * 50ms)
          let attempts = 0;

          const pollForProduct = () => {
            attempts++;
            const productCard = document.querySelector(`[data-product-id="${productData.productId}"]`);

            if (productCard) {

              // Helper function to calculate total header height
              const calculateHeaderHeight = () => {
                const headerSelectors = [
                  'sticky-header',
                  '[data-sticky-header]',
                  'header.sticky',
                  '.header--sticky',
                  'header[role="banner"]',
                  '.header',
                  'header',
                  '.announcement-bar',
                  '.filters-toolbar',
                  '.collection-filters'
                ];

                let totalHeaderHeight = 0;
                const foundHeaders = [];

                // Check all possible sticky elements at top of page
                for (const selector of headerSelectors) {
                  const element = document.querySelector(selector);
                  if (element) {
                    const rect = element.getBoundingClientRect();
                    const styles = window.getComputedStyle(element);
                    const isSticky = styles.position === 'sticky' || styles.position === 'fixed';

                    // Only count if it's actually at the top
                    if (rect.top <= 50 && isSticky) {
                      foundHeaders.push({
                        selector,
                        height: element.offsetHeight,
                        top: rect.top,
                        bottom: rect.bottom
                      });
                      totalHeaderHeight = Math.max(totalHeaderHeight, rect.bottom);
                    }
                  }
                }

                return {
                  totalHeaderHeight,
                  foundHeaders,
                  finalHeight: totalHeaderHeight > 0 ? totalHeaderHeight + 20 : 120
                };
              };

              // Helper function to scroll product into view
              const scrollProductIntoView = (headerHeight) => {
                const productRect = productCard.getBoundingClientRect();
                const scrollTop = window.scrollY || window.pageYOffset;
                const targetScroll = scrollTop + productRect.top - headerHeight - 20; // 20px padding

                window.scrollTo({
                  top: Math.max(0, targetScroll),
                  behavior: 'instant'
                });
              };

              // Wait for sticky headers/filters to fully initialize and settle
              // Single scroll after everything is ready - no jumping
              setTimeout(() => {
                const headerInfo = calculateHeaderHeight();
                scrollProductIntoView(headerInfo.finalHeight);

                // Clear the clicked product from storage after restoration
                sessionStorage.removeItem(productKey);
              }, 700); // Wait 700ms for sticky elements to fully settle
            } else if (attempts < maxAttempts) {
              // Try again in 50ms (faster polling)
              setTimeout(pollForProduct, 50);
            } else {
              sessionStorage.removeItem(productKey);
            }
          };

          // Start polling immediately
          pollForProduct();

          return; // Exit early - product restoration takes priority
        } else {
          // Expired, remove it
          sessionStorage.removeItem(productKey);
        }
      }

      // PRIORITY 2: General scroll position restoration
      const key = `chunked-scroll:${window.location.pathname}`;
      const stateStr = sessionStorage.getItem(key);

      if (!stateStr) return;

      const state = JSON.parse(stateStr);

      // Only restore if recent (within 5 minutes)
      if (Date.now() - state.timestamp > 5 * 60 * 1000) {
        sessionStorage.removeItem(key);
        return;
      }

      // Restore scroll position
      if (state.scrollOffset) {
        window.scrollTo({
          top: state.scrollOffset,
          behavior: 'instant',
        });
      }
    } catch (error) {
      console.error('Failed to restore scroll state:', error);
    }
  }

  /**
   * Show/hide bottom loading indicator
   * @param {boolean} show
   */
  #showLoading(show) {
    const { loadingIndicator } = this.refs;
    if (!loadingIndicator) return;

    if (show) {
      loadingIndicator.style.display = 'flex';
    } else {
      loadingIndicator.style.display = 'none';
    }
  }

  /**
   * Show/hide top loading indicator (for loading previous chunks)
   * @param {boolean} show
   */
  #showTopLoading(show) {
    const { topLoadingIndicator } = this.refs;
    if (!topLoadingIndicator) return;

    if (show) {
      topLoadingIndicator.style.display = 'flex';
    } else {
      topLoadingIndicator.style.display = 'none';
    }
  }

  /**
   * Show end of collection indicator
   */
  #showEndIndicator() {
    const { endIndicator } = this.refs;

    if (endIndicator) {
      endIndicator.style.display = 'block';
    }
  }

  /**
   * Show Load More button
   */
  #showLoadMoreButton() {
    const { loadMoreContainer } = this.refs;

    if (loadMoreContainer) {
      loadMoreContainer.style.display = 'flex';
    }
  }

  /**
   * Hide Load More button
   */
  #hideLoadMoreButton() {
    const { loadMoreContainer } = this.refs;

    if (loadMoreContainer) {
      loadMoreContainer.style.display = 'none';
    }
  }

  /**
   * Show retry button on error
   * @param {'next' | 'previous'} direction
   */
  #showRetry(direction) {
    // TODO: Implement retry UI
  }

  /**
   * Handle pageshow event for bfcache restoration (iOS Safari swipe-back)
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (event.persisted) {
      this.#restoreState();
    }
  };

  /**
   * Reset scroll state (called on filter change)
   */
  #handleReset = () => {
    // Cancel any in-flight requests
    if (this.#abortController) {
      this.#abortController.abort();
    }

    // Clear cache
    this.#chunkCache.clear();

    // Clear state
    this.#chunksInDom = [];
    this.#fetchingNext = false;
    this.#fetchingPrevious = false;

    // Clear sessionStorage
    const key = `chunked-scroll:${window.location.pathname}`;
    sessionStorage.removeItem(key);

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * Cleanup on disconnect
   */
  #cleanup() {
    if (this.#topObserver) {
      this.#topObserver.disconnect();
      this.#topObserver = null;
    }

    if (this.#abortController) {
      this.#abortController.abort();
    }

    if (this.#parentElement) {
      this.#parentElement.removeEventListener('chunked-scroll:reset', this.#handleReset);
    }

    window.removeEventListener('pageshow', this.#handlePageShow);

    // Clear references
    this.#chunksInDom = [];
    this.#chunkCache.clear();
  }

  /**
   * Set aspect ratio processor callback
   * @param {Function} fn - Callback function
   */
  setAspectRatioProcessor(fn) {
    this.#processNewElements = fn;
  }
}
